'use client';

import { useEffect, useRef } from 'react';
import useTokenManager from '@/hooks/useTokenManager';
import { getTokenManager } from '@/hooks/getTokenManager';

/**
 * Composant invisible qui s'occupe de synchroniser les tokens entre localStorage et cookies,
 * et de rafraîchir automatiquement les tokens expirés.
 * Ce composant doit être placé au niveau racine de l'application.
 */
export function TokenSynchronizer() {
  // Utiliser useTokenManager pour les hooks React et getTokenManager pour les fonctions non-React
  const tokenManagerHook = useTokenManager();
  const originalFetchRef = useRef<typeof window.fetch | null>(null);
  
  useEffect(() => {
    console.log('✅ TokenSynchronizer: Initialisation');
    
    // Enregistrer la fonction fetch originale
    if (!originalFetchRef.current) {
      originalFetchRef.current = window.fetch;
    }
    const originalFetch = originalFetchRef.current || window.fetch;
    
    // Interception des appels fetch pour ajouter le token d'authentification
    window.fetch = async function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      // Ne pas modifier les requêtes vers des domaines externes
      if (typeof input === 'string' && (
          input.startsWith('http') && 
          !input.includes('cyber-threat-consulting.com') && 
          !input.includes(window.location.host)
      )) {
        return originalFetch(input, init);
      }
      
      try {
        // Récupérer TokenManager de manière sécurisée via getTokenManager
        const TokenManager = await getTokenManager();
        
        // Ajouter le token d'authentification aux en-têtes si disponible
        const token = TokenManager.getAccessToken();
        if (token) {
          const headers = new Headers(init?.headers || {});
          headers.set('Authorization', `Bearer ${token}`);
          headers.set('x-sentinel-token', token);
          
          init = {
            ...init,
            headers
          };
        }
      } catch (error) {
        console.error('Erreur lors de la récupération du token:', error);
      }
      
      // Exécuter la requête originale avec les nouveaux en-têtes
      return originalFetch(input, init);
    };
    
    // Nettoyage lors du démontage du composant
    return () => {
      // Restaurer la fonction fetch originale si elle existe
      if (originalFetchRef.current) {
        window.fetch = originalFetchRef.current;
      }
      
      console.log('✅ TokenSynchronizer: Arrêt du composant, fetch restauré');
    };
  }, [tokenManagerHook]);
  
  // Ce composant ne rend rien, il effectue seulement une synchronisation des tokens
  return null;
}

export default TokenSynchronizer;
